import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, Globe2, X, Sparkles } from "lucide-react";

/**
 * CommunityCreateModal — extended create form with visibility (public /
 * private) + join_policy (open / request) toggles. Rendered as a full
 * screen sheet on mobile, centered dialog on desktop.
 */
export default function CommunityCreateModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [joinPolicy, setJoinPolicy] = useState("open");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => {
    setName(""); setDescription(""); setCoverUrl("");
    setVisibility("public"); setJoinPolicy("open");
  };

  const submit = async () => {
    if (name.trim().length < 2) {
      toast.error("Give your community a name (at least 2 characters).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/communities", {
        name: name.trim(),
        description: description.trim(),
        cover_url: coverUrl.trim() || null,
        visibility,
        // Private communities implicitly require approval — set the policy
        // to "request" behind the scenes even if the user forgot to flip
        // it, so we never end up with a discoverable-but-open private group.
        join_policy: visibility === "private" ? "request" : joinPolicy,
      });
      toast.success(`${data.name} created!`);
      reset();
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="community-create-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-rose-400" />
            <div className="text-sm font-semibold text-white">Start a community</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
            data-testid="community-create-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              data-testid="community-create-name"
              placeholder="Panda Family"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm outline-none focus:border-rose-500"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block flex items-center justify-between">
              Description
              <span className="normal-case tracking-normal text-zinc-600">{description.length}/1000</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
              data-testid="community-create-description"
              rows={3}
              placeholder="What's this community about?"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm outline-none focus:border-rose-500 resize-none"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">Cover URL (optional)</label>
            <input
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              data-testid="community-create-cover"
              placeholder="https://…"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm outline-none focus:border-rose-500 font-mono"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 block">Visibility</label>
            <div className="grid grid-cols-2 gap-2">
              <VisibilityChoice
                active={visibility === "public"}
                onClick={() => setVisibility("public")}
                testid="community-create-visibility-public"
                icon={Globe2}
                label="Public"
                sub="Anyone can find it"
              />
              <VisibilityChoice
                active={visibility === "private"}
                onClick={() => setVisibility("private")}
                testid="community-create-visibility-private"
                icon={Lock}
                label="Private"
                sub="Invite-only, gated"
              />
            </div>
          </div>
          {visibility === "public" && (
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 block">
                How do people join?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <VisibilityChoice
                  active={joinPolicy === "open"}
                  onClick={() => setJoinPolicy("open")}
                  testid="community-create-policy-open"
                  icon={Globe2}
                  label="Open"
                  sub="Instant join"
                />
                <VisibilityChoice
                  active={joinPolicy === "request"}
                  onClick={() => setJoinPolicy("request")}
                  testid="community-create-policy-request"
                  icon={Lock}
                  label="Request"
                  sub="You approve"
                />
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-white/10 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || name.trim().length < 2}
            data-testid="community-create-submit"
            className="bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-semibold rounded-full px-5 py-2 text-sm flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function VisibilityChoice({ active, onClick, testid, icon: Icon, label, sub }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left border rounded-md px-3 py-2.5 transition ${
        active
          ? "border-rose-500 bg-rose-500/10 text-white"
          : "border-white/10 bg-black/30 text-zinc-400 hover:border-white/25"
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} />
        <div className="text-sm font-semibold">{label}</div>
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>
    </button>
  );
}
